import { hexToRgb, mixRgb } from './math';

/**
 * Off-screen canvases baked once and blitted per frame.
 *
 * Every sprite here replaces a per-frame `createRadialGradient` plus fill with a
 * single `drawImage`. Gradient stops scale linearly with alpha, so drawing a
 * baked sprite under `globalAlpha` reproduces the original exactly — this is a
 * pure performance change, not a visual one.
 */

const createCanvas = (width: number, height: number): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  return canvas;
};

/**
 * The six soft radial discs that make up one cloud puff, as
 * `[offsetX, offsetY, radius, alpha]` in units of the base radius. Each disc
 * fades to transparent so overlaps blend with no hard edges.
 */
const PUFF_DISCS = [
  [0, 0, 1.3, 1],
  [-1.25, 0.3, 0.95, 0.9],
  [1.3, 0.22, 1.0, 0.9],
  [0.1, -0.45, 0.85, 0.85],
  [0.85, -0.18, 0.7, 0.8],
  [-0.65, -0.12, 0.7, 0.8],
];

export const PUFF_WIDTH = 116;
export const PUFF_HEIGHT = 80;

/** Bake a cloud puff tinted for the given storm level (0 to 1). */
export const bakeCloudPuff = (storm: number): HTMLCanvasElement => {
  const tone = mixRgb(
    mixRgb(hexToRgb('FFFFFF'), hexToRgb('CAD4DC'), 0.35),
    hexToRgb('8A95A2'),
    storm * 0.6,
  );
  const [r, g, b] = tone.map((v) => Math.round(v));

  const canvas = createCanvas(PUFF_WIDTH, PUFF_HEIGHT);
  const ctx = canvas.getContext('2d');

  if (ctx == null) {
    return canvas;
  }

  const baseRadius = 24;
  const originX = 58;
  const originY = 40;

  PUFF_DISCS.forEach(([dx, dy, radiusScale, alpha]) => {
    const x = originX + dx * baseRadius;
    const y = originY + dy * baseRadius;
    const radius = radiusScale * baseRadius;

    const gradient = ctx.createRadialGradient(
      x,
      y,
      radius * 0.12,
      x,
      y,
      radius,
    );
    gradient.addColorStop(0, `rgba(${r},${g},${b},${alpha})`);
    gradient.addColorStop(
      0.55,
      `rgba(${r},${g},${b},${(alpha * 0.82).toFixed(3)})`,
    );
    gradient.addColorStop(1, `rgba(${r},${g},${b},0)`);

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, 7);
    ctx.fill();
  });

  return canvas;
};

/**
 * The sun/moon glint: one soft light column, baked once and drawn per frame as
 * a single clipped `drawImage`.
 */
export const bakeGlint = (): HTMLCanvasElement => {
  const canvas = createCanvas(64, 64);
  const ctx = canvas.getContext('2d');

  if (ctx == null) {
    return canvas;
  }

  const gradient = ctx.createRadialGradient(32, 6, 2, 32, 6, 58);
  gradient.addColorStop(0, 'rgba(255,252,240,0.95)');
  gradient.addColorStop(0.4, 'rgba(255,252,240,0.35)');
  gradient.addColorStop(1, 'rgba(255,252,240,0)');

  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 64, 64);

  return canvas;
};

/**
 * Nav-light glows, baked once per colour. There are only a handful of colours
 * across all six vessels, so this cache never grows.
 */
const glowCache = new Map<string, HTMLCanvasElement>();

export const glowSpriteFor = (rgb: string): HTMLCanvasElement => {
  const cached = glowCache.get(rgb);

  if (cached != null) {
    return cached;
  }

  const canvas = createCanvas(64, 64);
  const ctx = canvas.getContext('2d');

  if (ctx != null) {
    const gradient = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
    gradient.addColorStop(0, `rgba(${rgb},1)`);
    gradient.addColorStop(0.45, `rgba(${rgb},0.4)`);
    gradient.addColorStop(1, `rgba(${rgb},0)`);

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 64, 64);
  }

  glowCache.set(rgb, canvas);

  return canvas;
};
